import { Fragment } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import type { AccessCatalog } from "types/interfaces/access";
import {
  GUIDE_TIERS,
  tierGuide,
  tierName,
  type GuideCell,
} from "./tierGuideModel";

interface Props {
  open: boolean;
  onClose: () => void;
  catalog: AccessCatalog;
}

function Cell({ value }: { value: GuideCell }) {
  if (value === "yes") {
    return (
      <Check
        fontSize="small"
        aria-label="Allowed"
        sx={{ color: "#185059", verticalAlign: "middle" }}
      />
    );
  }
  if (value === "no") {
    return (
      <Typography
        variant="body2"
        color="text.disabled"
        aria-label="Not allowed"
      >
        -
      </Typography>
    );
  }
  return (
    <Typography variant="caption" color="text.disabled">
      not used
    </Typography>
  );
}

/** Every page, and what View, Contribute and Manage allow on it, action by
 * action - for whoever sets a group's tiers. */
export default function TierGuideDialog({ open, onClose, catalog }: Props) {
  const sections = tierGuide(catalog);
  const superAdmin = catalog.super_admin_actions ?? [];
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="What each tier allows"
      maxWidth="md"
      fullScreenBelow="md"
      showCloseIcon
      actions={
        <CustomButton fullWidth={false} onClick={onClose}>
          Close
        </CustomButton>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A tier opens a page and says what a member may do there; each tier
        includes everything the tier before it allows. No access hides the page
        completely. Which clients the pages show is set under Clients.
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Page and action</TableCell>
              {GUIDE_TIERS.map((tier) => (
                <TableCell
                  key={tier}
                  align="center"
                  sx={{ fontWeight: 700, width: 104 }}
                >
                  {tierName(tier)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sections.map((section) => (
              <Fragment key={section.key}>
                <TableRow>
                  <TableCell
                    colSpan={GUIDE_TIERS.length + 1}
                    sx={{ bgcolor: "grey.100", py: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#185059" }}
                    >
                      {section.name}
                    </Typography>
                    {section.note && (
                      <Typography variant="caption" color="text.secondary">
                        {section.note}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                {section.rows.map((row) => (
                  <TableRow key={`${section.key}-${row.key}`}>
                    <TableCell sx={{ pl: 3 }}>
                      <Typography variant="body2">{row.label}</Typography>
                    </TableCell>
                    {GUIDE_TIERS.map((tier) => (
                      <TableCell key={tier} align="center">
                        <Cell value={row.cells[tier]} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Box>
      {superAdmin.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Super Admin only, whatever the tiers
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {superAdmin.map((action) => (
              <li key={action.key}>
                <Typography variant="body2">{action.label}</Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
      <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {catalog.ai_agent.name}
        </Box>{" "}
        is a switch on the person, not a tier: {catalog.ai_agent.grants}.
      </Typography>
    </AppDialog>
  );
}
